"use client";

import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

import styles from "./VideoPlayer.module.css";

interface IVideoPlayerProps {
  url: string;
  playing?: boolean;
  title?: string;
  className?: string;
  onDuration?: (seconds: number) => void;
}

const noop = () => {};

const VideoPlayer = ({
  url,
  playing = false,
  title,
  className,
  onDuration = noop,
}: IVideoPlayerProps) => {
  const classnames = clsx(styles["video-player"], className);

  const isYoutube = url.includes("youtube") || url.includes("youtu.be");
  const isVimeo = url.includes("vimeo");

  return (
    <div className={classnames}>
      {isYoutube && (
        <YoutubePlayer title={title} url={url} playing={playing} onDuration={onDuration} />
      )}
      {isVimeo && <VimeoPlayer title={title} url={url} playing={playing} onDuration={onDuration} />}
    </div>
  );
};

export default VideoPlayer;

interface EmbeddedPlayerProps {
  title?: string;
  url: string;
  playing: boolean;
  onDuration: (seconds: number) => void;
}

const ensureUrlHasProtocol = (rawUrl: string) =>
  rawUrl.startsWith("http://") || rawUrl.startsWith("https://") ? rawUrl : `https://${rawUrl}`;

const sanitizeYoutubeId = (candidate?: string | null) => {
  if (!candidate) return null;
  const trimmed = candidate.trim();
  return trimmed.length === 11 ? trimmed : null;
};

const getYoutubeVideoId = (videoUrl: string) => {
  try {
    const parsedUrl = new URL(ensureUrlHasProtocol(videoUrl));
    const hostname = parsedUrl.hostname.toLowerCase();
    const isYoutubeHost =
      hostname === "youtu.be" ||
      hostname.endsWith("youtube.com") ||
      hostname.endsWith("youtube-nocookie.com");

    if (!isYoutubeHost) {
      return null;
    }

    if (hostname === "youtu.be") {
      return sanitizeYoutubeId(parsedUrl.pathname.split("/").filter(Boolean)[0]);
    }

    const paramId = sanitizeYoutubeId(parsedUrl.searchParams.get("v"));
    if (paramId) {
      return paramId;
    }

    const segments = parsedUrl.pathname.split("/").filter(Boolean);
    if (!segments.length) {
      return null;
    }

    return sanitizeYoutubeId(segments[segments.length - 1]);
  } catch {
    return null;
  }
};

const YoutubePlayer = ({ title, url, playing, onDuration }: EmbeddedPlayerProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [videoTitle, setVideoTitle] = useState(() => (title ? `Spela videoklipp: ${title}` : ""));

  const videoId = getYoutubeVideoId(url);

  useIframeApi();
  useYoutubeDuration(iframeRef, Boolean(videoId), onDuration);

  useEffect(() => {
    if (!videoId || title) return;

    let isMounted = true;

    fetch(`https://www.youtube-nocookie.com/oembed?url=${url}`)
      .then((response) => response.json())
      .then((json) => {
        if (isMounted) {
          setVideoTitle(`Spela videoklipp: ${json.title}`);
        }
      })
      .catch(() => {
        /* no-op on fetch failure */
      });

    return () => {
      isMounted = false;
    };
  }, [title, url, videoId]);

  useEffect(() => {
    if (!iframeRef.current) return;

    iframeRef.current.contentWindow?.postMessage(
      JSON.stringify({
        event: "command",
        func: playing ? "playVideo" : "pauseVideo",
      }),
      "*",
    );
  }, [playing]);

  const iframeOriginQuery = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const encodedOrigin = encodeURIComponent(window.location.origin);
    return `&origin=${encodedOrigin}`;
  }, []);

  if (!videoId) return null;
  return (
    <iframe
      title={videoTitle}
      ref={iframeRef}
      src={`https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1${iframeOriginQuery}`}
      width="100%"
      height="100%"
      allow="autoplay; fullscreen"
      allowFullScreen
    ></iframe>
  );
};

const VimeoPlayer = ({ title, url, playing, onDuration }: EmbeddedPlayerProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [videoTitle, setVideoTitle] = useState(() => (title ? `Spela videoklipp: ${title}` : ""));

  const videoMatch = url.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/);
  const videoId = Array.isArray(videoMatch) && videoMatch[1];

  useEffect(() => {
    if (!videoId) return;

    let isMounted = true;

    fetch(`https://vimeo.com/api/oembed.json?url=${url}`)
      .then((response) => response.json())
      .then((json) => {
        if (!isMounted) return;

        if (!title && json.title) {
          setVideoTitle(`Spela videoklipp: ${json.title}`);
        }
        if (typeof json.duration === "number") {
          onDuration(json.duration);
        }
      })
      .catch(() => {
        /* no-op on fetch failure */
      });

    return () => {
      isMounted = false;
    };
  }, [title, url, videoId, onDuration]);

  useEffect(() => {
    if (!iframeRef.current) return;

    iframeRef.current.contentWindow?.postMessage(
      JSON.stringify({
        method: playing ? "play" : "pause",
        value: true,
      }),
      "*",
    );
  }, [playing]);

  if (!videoId) return null;
  return (
    <iframe
      title={videoTitle}
      ref={iframeRef}
      src={`https://player.vimeo.com/video/${videoId}?api=1&dnt=1`}
      width="100%"
      height="100%"
      allow="autoplay; fullscreen"
      allowFullScreen
    ></iframe>
  );
};

const useIframeApi = () => {
  useEffect(() => {
    const iframeApiSrc = "https://www.youtube.com/iframe_api";

    if (!document.querySelector(`script[src="${iframeApiSrc}"]`)) {
      const iframeAPIScript = document.createElement("script");
      iframeAPIScript.src = "https://www.youtube.com/iframe_api";
      iframeAPIScript.async = true;
      document.body.appendChild(iframeAPIScript);
    }
  }, []);
};

const isYoutubeMessageOrigin = (origin: string) =>
  origin.endsWith("youtube.com") || origin.endsWith("youtube-nocookie.com");

/**
 * Reads the video length directly from the source by listening to the YouTube
 * IFrame API's `infoDelivery` messages. Sending `{ event: "listening" }` opts
 * the embed into posting player state (including `duration`) back to us, so no
 * API key is required.
 */
const useYoutubeDuration = (
  iframeRef: React.RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  onDuration: (seconds: number) => void,
) => {
  useEffect(() => {
    if (!enabled) return;

    let resolved = false;

    const requestState = () => {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "listening", id: 1, channel: "widget" }),
        "*",
      );
    };

    const handleMessage = (event: MessageEvent) => {
      if (resolved || !isYoutubeMessageOrigin(event.origin)) return;

      let payload: { info?: { duration?: unknown } };
      try {
        payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }

      const duration = payload?.info?.duration;
      if (typeof duration === "number" && duration > 0) {
        resolved = true;
        window.clearInterval(pollId);
        onDuration(duration);
      }
    };

    window.addEventListener("message", handleMessage);
    // The embed may not be ready immediately, so keep asking until it answers.
    requestState();
    const pollId = window.setInterval(requestState, 1000);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearInterval(pollId);
    };
  }, [enabled, iframeRef, onDuration]);
};
